import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import RideMap from "../components/RideMap";
import { ShieldCheck, MapPin, Bike, Car } from "lucide-react";
import { useBrand } from "../lib/brand";

const STATUS_LABEL = {
  requested: "En attente d'un chauffeur…",
  accepted: "Chauffeur en route",
  completed: "Course terminée",
  cancelled: "Course annulée",
};

export default function TripTracker() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { name: brandName } = useBrand();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get(`/track/${token}`);
        if (!cancelled) { setData(r.data); setError(""); }
      } catch {
        if (!cancelled) setError("Lien de suivi invalide ou expiré.");
      }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-6">
        <div data-testid="track-error" className="text-center">
          <p className="font-head font-bold text-xl text-[#1A1A1A]">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center text-[#75807A]">Chargement du suivi…</div>;
  }

  const live = data.driver_lat != null;
  const VIcon = data.vehicle_type === "car" ? Car : Bike;

  return (
    <div data-testid="trip-tracker" className="min-h-screen bg-[#FDF8F0]">
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="flex items-center gap-2 text-[#D94F2B] font-head font-black text-xl">
          <ShieldCheck className="w-6 h-6" /> {brandName} · Suivi en direct
        </div>
        <div className="mt-1 text-sm text-[#75807A]">{data.passenger_name ? `Trajet de ${data.passenger_name}` : "Trajet partagé"}</div>

        <div data-testid="track-status" className="mt-4 rounded-2xl bg-[#1A1A1A] text-white px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-semibold flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${data.status === "accepted" ? "bg-[#16a34a] animate-pulse" : "bg-[#F26419]"}`} />
            {STATUS_LABEL[data.status] || data.status}
          </span>
          {live && data.driver_eta_min != null && (
            <span className="text-xs text-white/70">~{data.driver_eta_min} min</span>
          )}
        </div>

        {data.pickup_lat != null && data.dest_lat != null && (
          <div className="mt-4">
            <RideMap
              center={{ lat: data.pickup_lat, lng: data.pickup_lng }}
              pickup={{ lat: data.pickup_lat, lng: data.pickup_lng }}
              dest={{ lat: data.dest_lat, lng: data.dest_lng }}
              driver={live ? { lat: data.driver_lat, lng: data.driver_lng } : undefined}
              height={340}
            />
          </div>
        )}

        <div className="mt-4 rounded-2xl bg-white border border-[#E2DFD8] p-4">
          <div className="text-xs uppercase tracking-wide text-[#75807A]">Itinéraire</div>
          <div className="mt-1 flex items-center gap-2 text-sm text-[#1A1A1A]">
            <MapPin className="w-4 h-4 text-[#F26419]" /> {data.pickup || "—"} → {data.destination || "—"}
          </div>
          {data.driver_name && (
            <div className="mt-3 pt-3 border-t border-[#E2DFD8] flex items-center gap-2 text-sm">
              <VIcon className="w-4 h-4 text-[#1A1A1A]" />
              <span className="font-semibold text-[#1A1A1A]">{data.driver_name}</span>
              {data.driver_plate && <span className="font-mono font-bold text-[#1A1A1A] bg-[#F5C518] px-2 py-0.5 rounded-md text-xs">{data.driver_plate}</span>}
            </div>
          )}
        </div>

        <div className="mt-6 text-center text-xs text-[#75807A]">Actualisé automatiquement · {brandName}</div>
      </div>
    </div>
  );
}
